/**
 * HustleRoundupLobbyPage — Sprint 1 lobby, refreshed for Sprint 2.
 *
 * URL: /meet
 *
 * Shows the user's scheduled rooms + lets them jump into an instant room.
 * Sends them via /meet/prejoin/:roomName so devices are chosen first.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Video, ArrowRight, Shuffle, Calendar, Plus } from "lucide-react";
import { apiFetch, fetchMe } from "@/lib/api";

const WORDS = [
  "aurora", "boomerang", "coral", "dingo", "eucalypt", "flint",
  "gecko", "harbour", "iron", "jarrah", "kelpie", "lagoon",
  "moray", "nectar", "opal", "possum", "quandong", "reef",
  "sandstone", "tinder", "uluru", "vagabond", "wattle", "yarra",
];
const randomRoom = () => {
  const p = () => WORDS[Math.floor(Math.random() * WORDS.length)];
  return `${p()}-${p()}-${Math.floor(Math.random() * 1000)}`;
};

export default function HustleRoundupLobbyPage() {
  const [room, setRoom]     = useState("");
  const [me, setMe]         = useState(null);
  const [myRooms, setMR]    = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const u = await fetchMe();
      if (!u?.id) { nav("/login?next=/meet"); return; }
      setMe(u);
      try {
        const list = await apiFetch("/hustleroundup/rooms/mine");
        setMR(list || []);
      } catch { /* ignore */ }
    })();
  }, [nav]);

  const go = () => {
    const slug = (room || randomRoom())
      .toLowerCase().trim().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
    if (slug) nav(`/meet/prejoin/${slug}`);
  };

  return (
    <div
      data-testid="hustleroundup-lobby"
      className="min-h-screen bg-stone-950 text-white flex items-start justify-center px-6 py-14"
      style={{
        backgroundImage:
          "radial-gradient(circle at 25% 15%, rgba(16,185,129,0.14), transparent 40%)",
      }}
    >
      <div className="max-w-2xl w-full">
        <div className="flex items-center gap-3 mb-6">
          <Video className="w-9 h-9 text-emerald-400" />
          <div>
            <div className="text-xl font-bold tracking-tight">Start a meeting</div>
            <div className="text-xs text-stone-500">Signed in as {me?.email}</div>
          </div>
          <Link
            to="/schedule"
            data-testid="lobby-schedule-link"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-700 hover:border-emerald-500 text-xs"
          >
            <Calendar className="w-3.5 h-3.5" /> Schedule
          </Link>
        </div>

        <div className="rounded-2xl border border-stone-900 bg-stone-900/40 p-5">
          <label className="text-[11px] uppercase tracking-widest text-stone-400 block mb-2">Room name</label>
          <div className="flex gap-2">
            <input
              type="text"
              data-testid="hustleroundup-room-input"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && go()}
              placeholder="e.g. weekly-standup"
              autoFocus
              className="flex-1 px-3 py-2.5 rounded-lg bg-stone-950 border border-stone-800 focus:border-emerald-500 outline-none text-sm"
            />
            <button
              type="button"
              data-testid="hustleroundup-random-btn"
              onClick={() => setRoom(randomRoom())}
              className="px-3 rounded-lg border border-stone-800 text-stone-400 hover:text-white"
              title="Suggest a room name"
            >
              <Shuffle className="w-4 h-4" />
            </button>
          </div>
          <button
            type="button"
            data-testid="hustleroundup-join-btn"
            onClick={go}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold text-sm"
          >
            Join meeting <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* My rooms */}
        {myRooms.length > 0 && (
          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">My rooms</div>
            <ul data-testid="lobby-my-rooms" className="space-y-2">
              {myRooms.map((r) => (
                <li key={r.id}
                    data-testid={`lobby-room-${r.slug}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-stone-900 bg-stone-900/40 hover:border-stone-700">
                  <Video className="w-4 h-4 text-emerald-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{r.name}</div>
                    <div className="text-[11px] text-stone-500">
                      {r.kind} · code {r.join_code}
                      {r.scheduled_start && ` · ${new Date(r.scheduled_start).toLocaleString()}`}
                    </div>
                  </div>
                  <button
                    onClick={() => nav(`/meet/prejoin/${r.slug}`)}
                    data-testid={`lobby-open-${r.slug}`}
                    className="px-3 py-1.5 rounded-full bg-emerald-500 text-stone-950 text-xs font-semibold"
                  >
                    Open
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 flex items-center gap-2 text-[11px] text-stone-500">
          <Link to="/history" data-testid="lobby-history-link" className="hover:text-white underline">Meeting history</Link>
          <span>·</span>
          <Link to="/schedule" data-testid="lobby-new-room-link" className="hover:text-white underline inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> New scheduled room
          </Link>
        </div>
      </div>
    </div>
  );
}
